/**
 * A chat's messages, in the prototype's column.
 *
 * The person's messages are right-aligned cards; the assistant's text is open
 * on the panel, through `Markdown`, under what it thought and looked up. A
 * reply that kept its rounds is drawn in the order they happened: each
 * round's thinking, its words, then its lookups, calls and images. While it
 * is written, its end always says what is happening: a row running, the words
 * arriving, or "Thinking". A question it asked follows the text as one quiet line; its options are the
 * composer's to offer. Copy, Retry (the last reply only) and Branch from here
 * sit under it. Every other kind — steps, an approval, a question card, a
 * draft, an image — is handed to its card (`messages/*`, through
 * `CardProps`). The reply being written is drawn last, by the same component
 * and under the id it will be stored by, so an open reasoning panel stays open
 * when the stored reply takes its place.
 *
 * Words selected in a reply, stored or still being written, get a Quote
 * button; the person's message then carries them, drawn above its text.
 *
 * Agents' news, which Assist sends the chat as a message of its own, is one
 * quiet line for each agent, which opens it. Read-only, as an agent's own
 * chat is drawn in the Agents panel, only Copy is left.
 *
 * A PC's Dispatch thread (`dispatch`) is the same list with a time label
 * between bursts, a line under the last reply saying why it waits on the PC
 * (with Review, on a phone paired with the PC that may answer its sheet),
 * and the messages held for the PC after it.
 */

import { Fragment, useContext, useEffect, useLayoutEffect, useRef, useState, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { AnswerItem, Device, Message, MessageBranch, ToolRun } from '../api';
import { ds } from '../ds';
import { isLocal, memoryFor, type LiveImage, type LiveTurn } from '../hooks/useTurns';
import { ApprovalBlock, StepLimitBlock, ToolCalls, type Decision } from './ToolCalls';
import { Link } from '../router';
import { useApp } from '../state';
import { endedLine } from './agentsText';
import { heldLine, markerLabel, timeMarks, waitLine } from './dispatchText';
import { SentAttachments } from './Attachments';
import { CommandChip } from './composer/CommandChip';
import { BUILTIN_NAMES, COMMANDS, sentCommand } from './composer/slash';
import { Lookups } from './Lookups';
import { CodeText, Markdown } from './Markdown';
import { Reasoning } from './Reasoning';
import { busy as running, emptyRound, joinComputerRounds, QUIET_MS, roundViews } from './replyRounds';
import { ApprovalCard } from './messages/ApprovalCard';
import { ArtifactCard } from './messages/ArtifactCard';
import { OpenArtifact } from './ArtifactTile';
import { GeneratedImageView, isFresh } from './messages/GeneratedImageView';
import { ImageMessage } from './messages/ImageMessage';
import { QuestionCard } from './messages/QuestionCard';
import { StepsCard } from './messages/StepsCard';
import type { CardProps } from './messages/types';
import { quoteText } from './quoteText';
import { replyNote } from './replyNote';
import { hasBridge, offerQuote, onQuoteTap } from '../android';
import { DebugDialog } from './DebugDialog';
import { statsLine } from './settings/format';
import { Ph } from './ui';

function Action({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="bare hov-soft tap"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{ width: 28, height: 26, display: 'grid', placeItems: 'center', borderRadius: 6 }}
    >
      <Ph name={icon} size={14} />
    </button>
  );
}

function CopyAction({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <Action
      icon={copied ? 'check' : 'copy'}
      label={copied ? 'Copied' : 'Copy'}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => setCopied(false), 1500);
        });
      }}
    />
  );
}

/** The question dock's answers: each question, quiet, over its answer. */
function AnswerBlock({ items }: { items: readonly AnswerItem[] }) {
  return (
    <div
      className="selectable"
      style={{
        alignSelf: 'flex-end',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '11px 15px',
        background: 'var(--surface-card)',
        borderRadius: 'var(--radius-app-card)',
        border: '1px solid var(--border-subtle)'
      }}
    >
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 12.5, lineHeight: 1.45, color: 'var(--text-muted)', overflowWrap: 'break-word' }}>
            {item.question}
          </span>
          <span
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 7,
              fontSize: 14.5,
              lineHeight: 1.5,
              color: 'var(--text-strong)',
              overflowWrap: 'break-word'
            }}
          >
            <Ph name="arrow-bend-down-right" size={13} color="var(--text-faint)" style={{ flex: 'none' }} />
            <span style={{ minWidth: 0 }}>{item.answer}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/** `‹ 2 / 3 ›`: which version of a message is shown, and the way to the
 *  others. */
function Versions({ branch, onSwitch }: { branch: MessageBranch; onSwitch?: (id: string) => void }) {
  const step = (icon: string, label: string, to?: string) => (
    <button
      type="button"
      className="bare hov-soft tap"
      aria-label={label}
      title={label}
      disabled={!to || !onSwitch}
      onClick={() => to && onSwitch?.(to)}
      style={{
        width: 22,
        height: 24,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 6,
        opacity: to && onSwitch ? 1 : 0.35,
        cursor: to && onSwitch ? 'pointer' : 'default'
      }}
    >
      <Ph name={icon} size={12} />
    </button>
  );
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {step('caret-left', 'Previous version', branch.prev)}
      <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', padding: '0 2px' }}>
        {branch.at} / {branch.of}
      </span>
      {step('caret-right', 'Next version', branch.next)}
    </span>
  );
}

/** The text box a message is edited in, in place of the message. */
function EditBox({ initial, onCancel, onSend }: { initial: string; onCancel: () => void; onSend: (text: string) => void }) {
  const [draft, setDraft] = useState(initial);
  const box = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [draft]);
  useEffect(() => {
    const el = box.current;
    el?.focus();
    el?.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const changed = draft.trim() && draft.trim() !== initial.trim();
  const { Button } = ds();
  return (
    <div
      style={{
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 10,
        background: 'var(--surface-card)',
        borderRadius: 'var(--radius-app-card)',
        border: '1px solid var(--border-strong)'
      }}
    >
      <textarea
        ref={box}
        value={draft}
        aria-label="Edit message"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return;
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          } else if (e.key === 'Enter' && !e.shiftKey && !window.matchMedia?.('(pointer: coarse)').matches) {
            e.preventDefault();
            if (changed) onSend(draft.trim());
          }
        }}
        rows={1}
        style={{
          border: 0,
          background: 'transparent',
          resize: 'none',
          padding: '2px 4px',
          fontSize: 15,
          lineHeight: 1.6,
          color: 'var(--text-strong)'
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <Button variant="ghost" density="app" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" density="app" size="sm" disabled={!changed} onClick={() => changed && onSend(draft.trim())}>
          Send
        </Button>
      </div>
    </div>
  );
}

export function UserMessage({
  message,
  onEdit,
  onSwitch
}: {
  message: Message;
  /** Given where a message can be edited: sends the new version. */
  onEdit?: (text: string) => void;
  onSwitch?: (messageId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const { skills } = useApp();
  if (message.body.kind !== 'text') return null;
  const { text, attachments, answered, quote, artifact } = message.body;
  const { branch } = message;
  const stored = !isLocal(message.id);

  if (editing && onEdit) {
    return (
      <EditBox
        initial={text}
        onCancel={() => setEditing(false)}
        onSend={(next) => {
          setEditing(false);
          onEdit(next);
        }}
      />
    );
  }

  const actions =
    stored && (branch || text || onEdit) ? (
      <div
        className="msg-actions"
        style={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 2, color: 'var(--text-faint)', marginRight: -6 }}
      >
        {branch ? <Versions branch={branch} onSwitch={onSwitch} /> : null}
        {text && !answered?.length ? <CopyAction text={text} /> : null}
        {onEdit && !answered?.length ? <Action icon="pencil-simple" label="Edit" onClick={() => setEditing(true)} /> : null}
      </div>
    ) : null;

  if (answered?.length) {
    return (
      <div className="msg-hover" style={{ alignSelf: 'flex-end', maxWidth: '80%', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <AnswerBlock items={answered} />
        {actions}
      </div>
    );
  }

  const sent = sentCommand(text, [...COMMANDS, ...BUILTIN_NAMES, ...(skills ?? []).map((s) => s.name)]);
  return (
    <div
      className="msg-hover"
      style={{ alignSelf: 'flex-end', maxWidth: '80%', display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      {attachments?.length ? <SentAttachments attachments={attachments} /> : null}
      {text || quote ? (
        <div
          style={{
            alignSelf: 'flex-end',
            maxWidth: '100%',
            minWidth: 0,
            background: 'var(--surface-card)',
            borderRadius: 'var(--radius-app-card)',
            padding: '11px 15px',
            fontSize: 15,
            lineHeight: 1.6,
            color: 'var(--text-strong)',
            overflowWrap: 'anywhere'
          }}
        >
          {artifact ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 13, color: 'var(--text-muted)' }}>
              <Ph name="paint-brush" size={13} />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{artifact.title}</span>
            </span>
          ) : null}
          {quote ? <QuoteLines text={quote} lines={3} style={{ marginBottom: text ? 6 : 0 }} /> : null}
          <CodeText
            text={sent ? sent.rest : text}
            lead={sent ? <CommandChip command={sent.command} style={{ marginRight: sent.rest ? 7 : 0 }} /> : undefined}
          />
        </div>
      ) : null}
      {actions}
    </div>
  );
}

export function ThinkingLine() {
  return (
    <div
      role="status"
      style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text-faint)' }}
    >
      <Ph name="sparkle" size={15} color="var(--primary)" className="pulse" />
      Thinking
    </div>
  );
}

function MemoryNote() {
  return (
    <Link
      to="/settings/memory"
      className="hov-strong"
      style={{
        alignSelf: 'flex-start',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12.5,
        color: 'var(--text-faint)'
      }}
    >
      <Ph name="brain" size={14} />
      Memory updated
    </Link>
  );
}

/** The question a reply ends with. Answered from the composer. */
function AskLine({ question }: { question: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        fontSize: 14,
        lineHeight: 1.5,
        color: 'var(--text-muted)'
      }}
    >
      <Ph name="question" size={15} color="var(--text-faint)" style={{ flex: 'none', marginTop: 3 }} />
      <span className="selectable" style={{ minWidth: 0, overflowWrap: 'break-word' }}>{question}</span>
    </div>
  );
}

/** The end of a live reply once nothing has arrived for a while: the model is
 *  still at work on something it does not show. Mounted afresh (by key) each
 *  time something arrives. */
function Quiet({ since }: { since: number }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShown(true), Math.max(0, since + QUIET_MS - Date.now()));
    return () => clearTimeout(timer);
  }, [since]);
  return shown ? <Reasoning text="" seconds={null} thinking since={since} /> : null;
}

function AssistantText({
  chatId,
  message,
  live = false,
  clock,
  lookupsRunning,
  liveImages,
  liveCalls,
  onDecide,
  actions
}: {
  /** For a developer's Details. */
  chatId?: string;
  message: Message;
  /** Still being written. */
  live?: boolean;
  /** While it is written: when its current round began, and when anything
   *  last arrived (ms). */
  clock?: { round: number; last: number };
  /** Indices into the reply's lookups still out, while it is written. */
  lookupsRunning?: readonly number[];
  /** While it is written: its images, made or still being made. */
  liveImages?: readonly (LiveImage | undefined)[];
  /** While it is written: its calls on the machine or a connector. */
  liveCalls?: readonly (ToolRun | undefined)[];
  /** Given where the reply waits for the person: their answer. */
  onDecide?: (decision: Decision) => void;
  actions: { retry?: () => void; branch?: () => void } | null;
}) {
  const { me, catalogue } = useApp();
  const [details, setDetails] = useState(false);
  if (message.body.kind !== 'text') return null;
  const { text, reasoning, thoughtSeconds, lookups = [], ask, memory, pending, stepLimit, files, rounds, steers = [] } = message.body;
  // A developer's figures, and the way to how the reply was made.
  const stats = me?.plan === 'developer' && !live ? message.stats : undefined;
  const calls: readonly (ToolRun | undefined)[] = liveCalls ?? message.body.calls ?? [];
  const approvals = (pending ?? []).filter((c) => c.needs === 'approval');
  const note = replyNote(message);
  const images: readonly (LiveImage | undefined)[] = liveImages ?? message.body.images ?? [];
  // A reply stored before rounds were kept is one view, drawn as it always was.
  const rounded = roundViews(rounds ?? [], {
    reasoning: reasoning ?? '',
    text,
    lookups: lookups.length,
    calls: calls.length,
    images: images.length,
    thoughtSeconds: thoughtSeconds ?? null
  });
  // Clicks and keys in a row are one group; a message sent between them is not
  // moved across it.
  const views = joinComputerRounds(rounded, calls, new Set(steers.map((st) => Math.min(st.round, rounded.length) - 1)));
  // What is running says so on its own row.
  const idle = live && !running(calls, lookupsRunning ?? [], images);
  const tail = views[views.length - 1]!;

  return (
    // A reply can be quoted while it is still being written.
    <div data-quote="" style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      {views.map((r, i) => {
        const words = r.text ? <Markdown text={r.text} /> : null;
        const [lf, lt] = r.lookups;
        const ran = calls.slice(...r.calls).filter((c): c is ToolRun => !!c);
        // Words go first in a round, as they happened. A reply stored before
        // rounds were kept draws its lookups and calls first; a reply of one
        // round has none, so its words stay in the same place from live to
        // stored and a selection in them survives `done`.
        const first = !!rounds || (lt === lf && !ran.length);
        return (
          <Fragment key={i}>
            <Reasoning
              text={r.reasoning}
              seconds={r.seconds}
              thinking={idle && r === tail && emptyRound(r)}
              since={clock?.round ?? null}
            />
            {first ? words : null}
            {lt > lf ? (
              <Lookups
                lookups={lookups.slice(lf, lt)}
                running={(lookupsRunning ?? []).filter((n) => n >= lf && n < lt).map((n) => n - lf)}
              />
            ) : null}
            {ran.length ? <ToolCalls calls={ran} /> : null}
            {first ? null : words}
            {images.slice(...r.images).map((image, j) =>
              image ? (
                <GeneratedImageView
                  key={`image-${r.images[0] + j}`}
                  fileId={image.fileId}
                  prompt={image.prompt}
                  width={image.width}
                  height={image.height}
                  failed={image.failed ?? false}
                  fresh={live || isFresh(message.createdAt)}
                />
              ) : null
            )}
            {steers
              .filter((st) => Math.min(st.round, rounded.length) === r.last + 1)
              .map((st, j) => (
                <SteerLine key={`steer-${j}`} text={st.text} />
              ))}
          </Fragment>
        );
      })}
      {idle && clock && !emptyRound(tail) ? <Quiet key={clock.last} since={clock.last} /> : null}
      {files?.length ? <SentAttachments attachments={files} align="start" /> : null}
      {ask ? ask.questions.map((q, i) => <AskLine key={i} question={q.question} />) : null}
      {approvals.length && onDecide && !live ? <ApprovalBlock calls={approvals} model={message.model} onDecide={onDecide} /> : null}
      {stepLimit && onDecide && !live ? <StepLimitBlock steps={stepLimit} onDecide={onDecide} /> : null}
      {actions ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: 'var(--text-faint)', marginLeft: -6 }}>
          {text ? <CopyAction text={text} /> : null}
          {actions.retry ? <Action icon="arrows-clockwise" label="Retry" onClick={actions.retry} /> : null}
          {actions.branch ? <Action icon="git-branch" label="Branch from here" onClick={actions.branch} /> : null}
          {stats && chatId ? <Action icon="info" label="Details" onClick={() => setDetails(true)} /> : null}
          {note ? <span style={{ fontSize: 12.5, marginLeft: 6 }}>{note}</span> : null}
        </div>
      ) : null}
      {stats ? (
        <span className="selectable" style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-faint)' }}>
          {statsLine(stats, catalogue?.models.find((m) => m.id === stats.model)?.short ?? stats.model.replace(/^[^:]+:/, ''))}
        </span>
      ) : null}
      {details && chatId ? <DebugDialog chatId={chatId} messageId={message.id} onClose={() => setDetails(false)} /> : null}
      {memory?.length || memoryFor(message.id) ? <MemoryNote /> : null}
    </div>
  );
}

/** What the person sent into a reply as it worked, where the reply read it:
 *  their words, small, on their side. */
function SteerLine({ text }: { text: string }) {
  return (
    <div
      data-steer=""
      className="selectable"
      style={{
        alignSelf: 'flex-end',
        maxWidth: '80%',
        minWidth: 0,
        background: 'var(--surface-card)',
        borderRadius: 'var(--radius-app-card)',
        padding: '7px 12px',
        fontSize: 13.5,
        lineHeight: 1.5,
        color: 'var(--text-muted)',
        overflowWrap: 'anywhere'
      }}
    >
      <CodeText text={text} />
    </div>
  );
}

/** Agents' news where it reached the chat: how each ended, and the way to it. */
function AgentsNews({
  agents,
  onOpen
}: {
  agents: NonNullable<Extract<Message['body'], { kind: 'text' }>['agents']>;
  onOpen?: (chatId: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {agents.map((a) => (
        <div key={a.chatId} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 24, fontSize: 13, color: 'var(--text-faint)' }}>
          <Ph name="users-three" size={14} style={{ flex: 'none' }} />
          <span style={{ minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{endedLine(a)}</span>
          {onOpen ? (
            <>
              <span aria-hidden="true">·</span>
              <button type="button" className="bare" onClick={() => onOpen(a.chatId)} style={{ flex: 'none', fontSize: 13, color: 'var(--text-link)' }}>
                Open
              </button>
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Assist's note that the chat began as a copy of a shared page, with the
 *  copy to open beside it. */
function RemixNote({ remix }: { remix: NonNullable<Extract<Message['body'], { kind: 'text' }>['remix']> }) {
  const open = useContext(OpenArtifact);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 24, fontSize: 13, color: 'var(--text-faint)' }}>
      <Ph name="shuffle" size={14} style={{ flex: 'none' }} />
      <span style={{ minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>Remixed “{remix.title}” from a shared page.</span>
      {open ? (
        <>
          <span aria-hidden="true">·</span>
          <button type="button" className="bare" onClick={() => open(remix.artifactId, 1)} style={{ flex: 'none', fontSize: 13, color: 'var(--text-link)' }}>
            Open
          </button>
        </>
      ) : null}
    </div>
  );
}

const CARDS: Partial<Record<Message['body']['kind'], ComponentType<CardProps>>> = {
  steps: StepsCard,
  approval: ApprovalCard,
  question: QuestionCard,
  artifact: ArtifactCard,
  image: ImageMessage
};

/** What the person can do with the messages; none, where they only watch. */
interface MessageActions {
  onRetry: (messageId: string) => void;
  /** Absent in a temporary chat, which offers no Branch. */
  onBranch?: (messageId: string) => void;
  /** A new version of one of the person's messages. */
  onEdit: (messageId: string, text: string) => void;
  /** Show another version of a message. */
  onSwitch: (messageId: string) => void;
  /** The person's answer to a reply waiting for them to allow a call. */
  onDecide: (message: Message, decision: Decision) => void;
  onChange: (message: Message) => void;
  onOpenArtifact: (message: Message) => void;
  /** Words picked from a reply with Quote, for the next message. */
  onQuote?: (text: string) => void;
}

interface ListProps {
  chatId: string;
  messages: Message[];
  /** The reply being written, or null when none is. */
  live: Pick<
    LiveTurn,
    | 'messageId'
    | 'text'
    | 'reasoning'
    | 'thoughtSeconds'
    | 'rounds'
    | 'roundSince'
    | 'lastAt'
    | 'lookups'
    | 'lookupsRunning'
    | 'images'
    | 'calls'
    | 'steers'
  > | null;
  busy: boolean;
  /** Open one of the chat's agents, from where its news reached the chat. */
  onOpenAgent?: (chatId: string) => void;
  /** A PC's thread: the PC, as far as the page knows it, the messages the
   *  Worker holds until it picks up, and, on a phone paired with it, opening
   *  the phone's own sheet for what the PC asks. */
  dispatch?: { device: Pick<Device, 'name' | 'online'> | null; held: readonly string[]; review?: () => void };
}

export function MessageList(props: ListProps & ({ readOnly: true } | MessageActions)) {
  const { chatId, messages, live, busy, onOpenAgent, dispatch } = props;
  const acts = 'readOnly' in props ? null : props;
  const last = messages[messages.length - 1];
  const marks = dispatch ? timeMarks(messages) : null;

  const rowOf = (m: Message): ReactNode => {
    if (m.role === 'user') {
      if (m.body.kind === 'text' && m.body.agents) return <AgentsNews key={m.id} agents={m.body.agents} onOpen={onOpenAgent} />;
      if (m.body.kind === 'text' && m.body.remix) return <RemixNote key={m.id} remix={m.body.remix} />;
      return (
        <UserMessage
          key={m.id}
          message={m}
          {...(!acts || busy || live ? {} : { onEdit: (text: string) => acts.onEdit(m.id, text), onSwitch: acts.onSwitch })}
        />
      );
    }

    if (m.body.kind === 'text') {
      const stored = !isLocal(m.id);
      return (
        <AssistantText
          key={m.id}
          chatId={chatId}
          message={m}
          {...(acts && m === last && !busy && !live ? { onDecide: (d: Decision) => acts.onDecide(m, d) } : {})}
          actions={
            acts
              ? {
                  ...(stored && !busy && m === last && !live ? { retry: () => acts.onRetry(m.id) } : {}),
                  ...(stored && !busy && acts.onBranch ? { branch: () => acts.onBranch!(m.id) } : {})
                }
              : {}
          }
        />
      );
    }

    const Card = CARDS[m.body.kind];
    return Card && acts ? (
      <Card key={m.id} chatId={chatId} message={m} onChange={acts.onChange} onOpenArtifact={acts.onOpenArtifact} />
    ) : null;
  };

  const rows: ReactNode[] = messages.flatMap((m) => {
    const at = marks?.get(m.id);
    const row = rowOf(m);
    return at === undefined ? [row] : [<TimeMark key={`time-${m.id}`} at={at} />, row];
  });

  // In the same list as the stored messages, so `done` swaps it for its stored
  // self in place rather than mounting a new one.
  if (live) {
    const reply: Message = {
      id: live.messageId ?? 'local-live',
      role: 'assistant',
      model: null,
      status: 'partial',
      createdAt: 0,
      body: {
        kind: 'text',
        text: live.text,
        reasoning: live.reasoning,
        ...(live.thoughtSeconds !== null ? { thoughtSeconds: live.thoughtSeconds } : {}),
        lookups: live.lookups,
        // Always in rounds while it is written, so each appears where it happened.
        rounds: live.rounds,
        ...(live.steers.length ? { steers: live.steers } : {})
      }
    };
    rows.push(
      <AssistantText
        key={reply.id}
        message={reply}
        live
        clock={{ round: live.roundSince, last: live.lastAt }}
        lookupsRunning={live.lookupsRunning}
        liveImages={live.images}
        liveCalls={live.calls}
        actions={null}
      />
    );
  }

  // What a reply in a PC's thread waits on, and what was sent meanwhile.
  if (dispatch && !live) {
    const wait = last?.role === 'assistant' && last.body.kind === 'text' ? last.body.deviceWait : undefined;
    const line = waitLine(wait, dispatch.device);
    if (line) {
      const review = wait?.state === 'asking' && dispatch.review ? { label: 'Review', onClick: dispatch.review } : undefined;
      rows.push(<StatusLine key="device-wait" icon={wait?.state === 'asking' ? 'hand-pointing' : 'plugs'} text={line} action={review} />);
    }
    dispatch.held.forEach((text, i) => rows.push(<UserMessage key={`held-${i}`} message={heldMessage(text, i)} />));
    if (dispatch.held.length) rows.push(<StatusLine key="device-held" icon="paper-plane-tilt" text={heldLine(dispatch.device?.name ?? 'Your PC')} align="end" />);
  }

  return (
    <>
      {rows}
      {acts?.onQuote ? <QuoteButton onQuote={acts.onQuote} /> : null}
    </>
  );
}

/** A message the Worker is holding for the PC, drawn as the person's own. */
const heldMessage = (text: string, i: number): Message => ({
  id: `local-held-${i}`,
  role: 'user',
  model: null,
  status: 'complete',
  createdAt: 0,
  body: { kind: 'text', text }
});

/** The time a burst of messages began, centred between them. */
function TimeMark({ at }: { at: number }) {
  return (
    <div role="separator" style={{ alignSelf: 'center', fontSize: 12, lineHeight: 1.4, color: 'var(--text-faint)', padding: '4px 0' }}>
      {markerLabel(at, Date.now() / 1000)}
    </div>
  );
}

/** One quiet line of where the thread stands, with the one thing to do about it. */
function StatusLine({
  icon,
  text,
  align = 'start',
  action
}: {
  icon: string;
  text: string;
  align?: 'start' | 'end';
  action?: { label: string; onClick: () => void } | undefined;
}) {
  return (
    <div
      role="status"
      style={{
        alignSelf: align === 'end' ? 'flex-end' : 'flex-start',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        fontSize: 13.5,
        lineHeight: 1.5,
        color: 'var(--text-muted)'
      }}
    >
      <Ph name={icon} size={15} color="var(--text-faint)" style={{ flex: 'none', marginTop: 3 }} />
      <span style={{ minWidth: 0, overflowWrap: 'break-word' }}>{text}</span>
      {action ? (
        <button type="button" className="bare" onClick={action.onClick} style={{ flex: 'none', fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-link)' }}>
          {action.label}
        </button>
      ) : null}
    </div>
  );
}

/** A quote, muted behind a rule and cut at `lines`; the whole of it on hover. */
export function QuoteLines({ text, lines, style }: { text: string; lines: number; style?: CSSProperties }) {
  return (
    <div
      className="selectable"
      title={text}
      style={{
        borderLeft: '2px solid var(--border-strong)',
        paddingLeft: 10,
        fontSize: 13.5,
        lineHeight: 1.5,
        color: 'var(--text-muted)',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'break-word',
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        ...style
      }}
    >
      {text}
    </div>
  );
}

/** The reply a selection lies in, when it starts and ends in one, and in
 *  none of the person's own words sent into it. */
function replyOf(range: Range): Element | null {
  const at = (node: Node) => (node instanceof Element ? node : node.parentElement);
  const inside = (node: Node) => at(node)?.closest('[data-quote]') ?? null;
  const theirs = (node: Node) => !!at(node)?.closest('[data-steer]');
  if (theirs(range.startContainer) || theirs(range.endContainer)) return null;
  const start = inside(range.startContainer);
  return start && start === inside(range.endContainer) ? start : null;
}

/**
 * Quote, over words selected in a reply: hands them to the composer. Above
 * the selection, or below its handles under a finger, where the system's own
 * menu sits above. Taken on the press, before a tap can clear the selection.
 *
 * In the Android app Quote is in the system's selection bar instead: the
 * words are offered to the app as they change, and come back when it is
 * tapped (android.ts). An app too old to offer it gets this button.
 */
function QuoteButton({ onQuote }: { onQuote: (text: string) => void }) {
  const [at, setAt] = useState<{ left: number; top: number; below: boolean; text: string } | null>(null);
  const [native, setNative] = useState(hasBridge);
  /** What the app was last offered. */
  const offered = useRef<string | null>(null);

  useEffect(() => {
    if (!native) return;
    const stop = onQuoteTap((text) => {
      onQuote(text);
      window.getSelection()?.removeAllRanges();
    });
    return () => {
      stop();
      if (offered.current !== null) void offerQuote(null);
      offered.current = null;
    };
  }, [native, onQuote]);

  useEffect(() => {
    let frame = 0;
    const read = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const selection = window.getSelection();
        const text = selection && !selection.isCollapsed && selection.rangeCount ? selection.toString() : '';
        const range = text.trim() ? selection!.getRangeAt(0) : null;
        const quotable = !!range && !!replyOf(range);
        if (native) {
          const next = quotable ? quoteText(text) : null;
          if (next !== offered.current) {
            offered.current = next;
            void offerQuote(next).then((ok) => {
              if (!ok) setNative(false);
            });
          }
          return;
        }
        if (!quotable) {
          setAt(null);
          return;
        }
        const box = range!.getBoundingClientRect();
        const touch = !!window.matchMedia?.('(pointer: coarse)').matches;
        const below = touch || box.top < 56;
        setAt({
          left: Math.min(Math.max(box.left + box.width / 2, 56), window.innerWidth - 56),
          // Under a finger, clear of the selection's handles.
          top: below ? box.bottom + (touch ? 32 : 8) : box.top - 8,
          below,
          text
        });
      });
    };
    read();
    document.addEventListener('selectionchange', read);
    window.addEventListener('scroll', read, true);
    window.addEventListener('resize', read);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('selectionchange', read);
      window.removeEventListener('scroll', read, true);
      window.removeEventListener('resize', read);
    };
  }, [native]);

  if (!at) return null;
  const take = () => {
    onQuote(quoteText(at.text));
    window.getSelection()?.removeAllRanges();
    setAt(null);
  };
  return createPortal(
    <button
      type="button"
      className="bare fade-in"
      onPointerDown={(e) => {
        e.preventDefault();
        take();
      }}
      onClick={take}
      style={{
        position: 'fixed',
        left: at.left,
        top: at.top,
        // `translate`, not `transform`, which fade-in's animation holds.
        translate: at.below ? '-50% 0' : '-50% -100%',
        zIndex: 40,
        height: 30,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '0 12px',
        borderRadius: 999,
        background: 'var(--surface-card)',
        border: '1px solid var(--border-strong)',
        boxShadow: 'var(--shadow-md)',
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--text-strong)'
      }}
    >
      <Ph name="quotes" size={14} />
      Quote
    </button>,
    document.body
  );
}
